import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/axios';
import type { ApiResponse } from '@vayu-saathi/shared-types';
import { applyOrgTheme } from '@/lib/theme-engine';
import { useAuthStore } from '@/stores/auth.store';

export interface OrgThemeConfig {
  id: string;
  primaryColor?: string | null;
  primaryForeground?: string | null;
  sidebarStyle: string;
  bgImageUrl?: string | null;
  backgroundColor?: string | null;
  cardColor?: string | null;
  appliedAt: string;
}

export interface OrgThemeData {
  themeConfig: OrgThemeConfig | null;
  logoUrl: string | null;
}

export interface OrgThemeInput {
  primaryColor: string | null;
  sidebarStyle: 'light' | 'dark' | 'branded';
  bgImageUrl: string | null;
  backgroundColor: string | null;
  cardColor: string | null;
  logoUrl: string | null;
}

const KEY = ['org', 'theme'] as const;

export function useOrgTheme() {
  const query = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<OrgThemeData>>('/org/theme');
      return res.data.data;
    },
  });

  // Apply theme whenever the config changes
  useEffect(() => {
    if (query.data) {
      applyOrgTheme(query.data.themeConfig);
    }
  }, [query.data]);

  return query;
}

/** Admins apply the theme directly — no approval step. */
export function useApplyOrgTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrgThemeInput) => {
      const res = await apiClient.put<ApiResponse<OrgThemeConfig>>('/org/theme', input);
      return res.data.data;
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: KEY });
      // The sidebar reads the logo from the signed-in user — keep it in sync.
      const { user, setUser } = useAuthStore.getState();
      if (user) setUser({ ...user, orgLogoUrl: input.logoUrl });
      toast.success('Theme applied');
    },
    meta: { errorMessage: 'Failed to apply theme' },
  });
}
